import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Tag, User, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { NEWS_ARTICLES } from '@/data/news';
import { NewsCard } from '@/components/NewsCard';
import { AdBanner } from '@/components/AdBanner';
import { NewsletterForm } from '@/components/NewsletterForm';
import { formatDate, ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { IMAGES } from '@/assets/images';

const NEWS_CATEGORIES = [
  { id: 'all', name: 'All News' },
  { id: 'technology', name: 'Technology' },
  { id: 'events', name: 'Events' },
  { id: 'cybersecurity', name: 'Cybersecurity' },
  { id: 'sustainability', name: 'Sustainability' },
  { id: 'regulations', name: 'Regulations' },
  { id: 'infrastructure', name: 'Infrastructure' },
  { id: 'industry', name: 'Industry' },
];

const ITEMS_PER_PAGE = 9;

export default function News() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const featuredArticle = useMemo(() => {
    return NEWS_ARTICLES.find(article => article.featured) || NEWS_ARTICLES[0];
  }, []);

  const filteredArticles = useMemo(() => {
    const nonFeatured = NEWS_ARTICLES.filter(article => article.id !== featuredArticle.id);
    if (selectedCategory === 'all') return nonFeatured;
    return nonFeatured.filter(
      article => article.category.toLowerCase() === selectedCategory.toLowerCase()
    );
  }, [selectedCategory, featuredArticle]);

  const totalPages = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE);
  const paginatedArticles = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredArticles.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredArticles, currentPage]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-background">
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative py-24 overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={featuredArticle.image}
            alt="Featured news background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <Badge className="mb-4 bg-primary text-primary-foreground">
              Featured Article
            </Badge>
            <Link
              to={`${ROUTE_PATHS.NEWS}/${featuredArticle.id}`}
              className="group block"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground group-hover:text-primary transition-colors">
                {featuredArticle.title}
              </h1>
              <p className="text-xl text-muted-foreground mb-6 leading-relaxed">
                {featuredArticle.excerpt}
              </p>
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-8">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{featuredArticle.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(featuredArticle.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  <span>{featuredArticle.category}</span>
                </div>
              </div>
              <Button size="lg" className="group-hover:scale-105 transition-transform">
                Read Full Article
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </motion.section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4">
          <AdBanner size="leaderboard" position="news-top" />
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold mb-8 text-foreground">Latest News & Updates</h2>
            <div className="flex flex-wrap gap-3">
              {NEWS_CATEGORIES.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  onClick={() => handleCategoryChange(category.id)}
                  className="transition-all"
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {paginatedArticles.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <NewsCard article={article} />
              </motion.div>
            ))}
          </div>

          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex justify-center items-center gap-2"
            >
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? 'default' : 'outline'}
                    onClick={() => setCurrentPage(page)}
                    className="w-10"
                  >
                    {page}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      <section className="py-12 border-y border-border">
        <div className="container mx-auto px-4">
          <AdBanner size="leaderboard" position="news-middle" />
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold mb-4 text-foreground">
              Stay Updated with Industry News
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Subscribe to our newsletter for the latest updates on airport security technology,
              industry trends, and upcoming events.
            </p>
            <NewsletterForm variant="default" />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
